import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  Button,
  VStack,
  HStack,
  Icon,
  SimpleGrid,
  Link,
} from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import { FaHistory, FaOm, FaBookReader, FaPlay, FaSpotify, FaYoutube } from 'react-icons/fa';
import { SiApplepodcasts } from 'react-icons/si';
import SEO from '../components/SEO';
import StatsCard from '../components/StatsCard';
import QuoteCarousel from '../components/QuoteCarousel';
import ScrollReveal from '../components/ScrollReveal';
import ScrollIndicator from '../components/ScrollIndicator';
import LatestEpisodes from '../components/LatestEpisodes';
import LotusDivider from '../components/LotusDivider';
import episodesData from '../data/episodes.json';

const HERO_IMAGES = [
  { src: '/images/hero-bg.jpg',        kb: 'kb-a', pos: 'center' },
  { src: '/images/hero-khajuraho.jpg', kb: 'kb-b', pos: 'center' },
  { src: '/images/hero-hampi.jpg',     kb: 'kb-c', pos: '25% center' },
];

const SLIDE_DURATION = 10000;
const FADE_DURATION  = 1800;

const HeroSlideshow = () => {
  const [current, setCurrent] = useState(0);
  const [visible, setVisible] = useState(HERO_IMAGES.map((_, i) => i === 0));

  useEffect(() => {
    const timer = setInterval(() => {
      const next = (current + 1) % HERO_IMAGES.length;
      // Fade in next
      setVisible(prev => prev.map((_, i) => i === next ? true : prev[i]));
      setTimeout(() => {
        // Fade out current
        const cur = current;
        setVisible(prev => prev.map((v, i) => i === cur ? false : v));
        setCurrent(next);
      }, FADE_DURATION);
    }, SLIDE_DURATION);
    return () => clearInterval(timer);
  }, [current]);

  return (
    <Box position="absolute" top={0} left={0} right={0} bottom={0} overflow="hidden">
      {HERO_IMAGES.map((img, i) => (
        <div
          key={img.src}
          className={`hero-slide ${img.kb}`}
          style={{
            backgroundImage: `url(${img.src})`,
            backgroundPosition: img.pos,
            opacity: visible[i] ? 0.78 : 0,
          }}
        />
      ))}
    </Box>
  );
};

const FeatureCard = ({ icon, title, description, glowColor = 'brand.500' }) => (
  <VStack
    align="center"
    spacing={5}
    p={8}
    borderRadius="2xl"
    background="rgba(40,24,8,0.95)"
    border="1px solid rgba(255,150,50,0.18)"
    backdropFilter="blur(12px)"
    transition="all 0.25s ease"
    position="relative"
    overflow="hidden"
    _hover={{
      transform: 'translateY(-6px)',
      border: '1px solid rgba(255,152,0,0.35)',
      boxShadow: '0 20px 60px rgba(255, 152, 0, 0.15), 0 0 0 1px rgba(255,152,0,0.1)',
      '& .feature-icon-wrap': {
        boxShadow: '0 0 30px rgba(255, 152, 0, 0.4)',
        transform: 'scale(1.1)',
      }
    }}
  >
    {/* Subtle top gradient accent */}
    <Box
      position="absolute"
      top={0}
      left="50%"
      transform="translateX(-50%)"
      w="60%"
      h="2px"
      bgGradient="linear(to-r, transparent, brand.500, transparent)"
    />
    <Box
      className="feature-icon-wrap"
      p={4}
      borderRadius="xl"
      bg="rgba(255,152,0,0.1)"
      border="1px solid rgba(255,152,0,0.2)"
      transition="all 0.25s ease"
    >
      <Icon as={icon} boxSize={8} color="brand.400" />
    </Box>
    <Heading
      as="h3"
      fontSize={{ base: "xl", md: "2xl" }}
      color="whiteAlpha.950"
      textAlign="center"
    >
      {title}
    </Heading>
    <Text
      color="whiteAlpha.700"
      textAlign="center"
      lineHeight="tall"
    >
      {description}
    </Text>
  </VStack>
);

const Home = () => {
  return (
    <>
      <Box id="home-container">
        <SEO
          title="Journey Through Indian History and Mythology"
          description="Explore the fascinating stories of Indian history and mythology through our engaging podcast series."
          keywords="indian history podcast, mythology stories, cultural heritage, storytelling, ancient india"
          canonicalUrl="/"
        />

        {/* ── Hero Section ── */}
        <Box
          position="relative"
          minH={{ base: "90vh", md: "85vh", lg: "calc(100vh - 64px)" }}
          display="flex"
          alignItems="center"
          overflow="hidden"
          bg="transparent"
        >
          {/* Hero Background Image */}
          <HeroSlideshow />

          {/* Gradient overlay — mobile: aggressive to hide image edge */}
          <Box
            display={{ base: 'block', md: 'none' }}
            position="absolute"
            top={0} left={0} right={0} bottom={0}
            bgGradient="linear(to-b, rgba(21,13,4,0.1) 0%, rgba(21,13,4,0.5) 35%, rgba(21,13,4,0.9) 52%, rgba(21,13,4,1) 63%)"
            zIndex={1}
          />
          {/* Gradient overlay — desktop: light to let Ken Burns breathe */}
          <Box
            display={{ base: 'none', md: 'block' }}
            position="absolute"
            top={0} left={0} right={0} bottom={0}
            bgGradient="linear(to-b, rgba(21,13,4,0.03) 0%, rgba(21,13,4,0.18) 45%, rgba(21,13,4,0.66) 72%, rgba(21,13,4,1) 90%)"
            zIndex={1}
          />
          {/* Soft radial backdrop behind the hero copy for readability over brighter art */}
          <Box
            display={{ base: 'none', md: 'block' }}
            position="absolute"
            top="8%" left="50%"
            transform="translateX(-50%)"
            w="880px" h="420px"
            bg="radial-gradient(ellipse closest-side, rgba(21,13,4,0.55), transparent)"
            zIndex={1}
            pointerEvents="none"
          />

          {/* Ambient glow orbs */}
          <Box
            position="absolute"
            top="15%"
            left="-5%"
            w={{ base: "280px", md: "450px" }}
            h={{ base: "280px", md: "450px" }}
            borderRadius="full"
            bg="rgba(255,152,0,0.12)"
            filter="blur(80px)"
            zIndex={1}
            pointerEvents="none"
          />
          <Box
            position="absolute"
            top="10%"
            right="-5%"
            w={{ base: "220px", md: "380px" }}
            h={{ base: "220px", md: "380px" }}
            borderRadius="full"
            bg="rgba(160,60,20,0.12)"
            filter="blur(80px)"
            zIndex={1}
            pointerEvents="none"
          />
          <Box
            position="absolute"
            bottom="20%"
            left="50%"
            transform="translateX(-50%)"
            w={{ base: "300px", md: "500px" }}
            h={{ base: "150px", md: "220px" }}
            borderRadius="full"
            bg="rgba(255,152,0,0.07)"
            filter="blur(60px)"
            zIndex={1}
            pointerEvents="none"
          />

          {/* Hero Content */}
          <Container maxW="container.xl" position="relative" zIndex={2}>
            <VStack spacing={{ base: 5, md: 6 }} align="center" textAlign="center" pb={{ base: 4, md: 6 }}>
              <ScrollReveal>
                <VStack spacing={4} maxW="3xl">
                  <Heading
                    as="h1"
                    fontSize={{ base: "3xl", sm: "4xl", md: "5xl", lg: "6xl" }}
                    lineHeight="1.15"
                    bgGradient="linear(to-r, brand.300, brand.500, terracotta.400)"
                    bgClip="text"
                    mb={{ base: 1, md: 1 }}
                    style={{
                      filter: 'drop-shadow(0 0 30px rgba(255,152,0,0.3))'
                    }}
                  >
                    Journey Through Indian History and Mythology
                  </Heading>

                  <LotusDivider />

                  <Text
                    fontSize={{ base: "md", sm: "lg", md: "xl", lg: "2xl" }}
                    color="whiteAlpha.800"
                    maxW="2xl"
                    lineHeight="1.6"
                  >
                    Discover the rich tapestry of Indian history and mythology, from ancient civilizations
                    to timeless epics, through immersive storytelling.
                  </Text>
                </VStack>
              </ScrollReveal>

              <ScrollReveal delay={0.2}>
                <Box maxW="4xl" mx="auto" w="100%">
                  <QuoteCarousel />
                </Box>
              </ScrollReveal>

              <ScrollReveal delay={0.35}>
                <HStack
                  spacing={{ base: 3, md: 4 }}
                  flexDirection={{ base: 'column', sm: 'row' }}
                  align={{ base: 'stretch', sm: 'center' }}
                  width="100%"
                  px={{ base: 4, md: 0 }}
                >
                  <Button
                    as={RouterLink}
                    to="/episodes"
                    colorScheme="brand"
                    size="lg"
                    leftIcon={<Icon as={FaPlay} />}
                    px={8}
                    boxShadow="0 8px 30px rgba(255,152,0,0.35)"
                    _hover={{
                      transform: 'translateY(-3px)',
                      boxShadow: '0 12px 40px rgba(255,152,0,0.5)',
                    }}
                    transition="all 0.25s"
                    width={{ base: '100%', sm: 'auto' }}
                  >
                    Start Listening
                  </Button>
                  <Button
                    as={RouterLink}
                    to="/about"
                    variant="outline"
                    colorScheme="whiteAlpha"
                    size="lg"
                    px={8}
                    borderColor="rgba(255,255,255,0.25)"
                    _hover={{
                      transform: 'translateY(-3px)',
                      bg: 'rgba(255,150,50,0.08)',
                      borderColor: 'rgba(255,255,255,0.4)',
                    }}
                    transition="all 0.25s"
                    width={{ base: '100%', sm: 'auto' }}
                  >
                    Learn More
                  </Button>
                </HStack>
              </ScrollReveal>

              <ScrollReveal delay={0.45}>
                <HStack spacing={{ base: 2, md: 3 }} flexWrap="nowrap" justify="center">
                    {[
                      { icon: FaSpotify, label: 'Spotify', href: 'https://open.spotify.com/show/03UrDnDHONZ92oOpWzfhxx', color: '#1DB954' },
                      { icon: SiApplepodcasts, label: 'Apple Podcasts', href: 'https://podcasts.apple.com/us/podcast/namaskar-india-tales-from-indian-history-and-mythology/id1516915527', color: '#B150E2' },
                      { icon: FaYoutube, label: 'YouTube', href: 'https://www.youtube.com/c/NamaskarIndia', color: '#FF0000' },
                    ].map(({ icon, label, href, color }) => (
                      <Link key={label} href={href} isExternal _hover={{ textDecoration: 'none' }}>
                        <HStack
                          spacing={{ base: 1, md: 2 }}
                          px={{ base: 3, md: 4 }}
                          py={2}
                          borderRadius="full"
                          border="1px solid rgba(255,255,255,0.12)"
                          background="rgba(255,255,255,0.05)"
                          backdropFilter="blur(8px)"
                          _hover={{
                            background: 'rgba(255,255,255,0.1)',
                            borderColor: color,
                            transform: 'translateY(-2px)',
                          }}
                          transition="all 0.2s"
                        >
                          <Icon as={icon} color={color} boxSize={{ base: 3, md: 4 }} />
                          <Text fontSize={{ base: "xs", md: "sm" }} color="whiteAlpha.900" fontWeight="medium">{label}</Text>
                        </HStack>
                      </Link>
                    ))}
                </HStack>
              </ScrollReveal>

            </VStack>
          </Container>
        </Box>

        <ScrollIndicator containerId="home-container" />

        {/* ── Latest Episodes ── */}
        <LatestEpisodes />

        {/* ── Stats Section ── */}
        <Box
          as="section"
          py={{ base: 4, md: 6 }}
          position="relative"
          overflow="hidden"
        >
          <Box className="glow-divider" mb={{ base: 4, md: 6 }} />

          {/* Background orbs */}
          <Box
            position="absolute"
            top="30%"
            left="-10%"
            w="350px"
            h="350px"
            borderRadius="full"
            bg="rgba(255,152,0,0.07)"
            filter="blur(80px)"
            pointerEvents="none"
          />
          <Box
            position="absolute"
            bottom="10%"
            right="-5%"
            w="280px"
            h="280px"
            borderRadius="full"
            bg="rgba(160,60,20,0.07)"
            filter="blur(70px)"
            pointerEvents="none"
          />

          <Container maxW="container.xl" position="relative">
            <VStack spacing={{ base: 6, md: 8 }}>
              <ScrollReveal>
                <VStack spacing={3}>
                  <Heading
                    as="h2"
                    fontSize={{ base: "2xl", md: "4xl" }}
                    textAlign="center"
                    variant="gradient"
                  >
                    Our Journey So Far
                  </Heading>
                  <Text
                    textAlign="center"
                    color="whiteAlpha.700"
                    fontSize={{ base: "md", md: "lg" }}
                    maxW="xl"
                    mx="auto"
                  >
                    Join thousands of listeners exploring India's rich history and mythology
                  </Text>
                </VStack>
              </ScrollReveal>
              <ScrollReveal delay={0.2}>
                <StatsCard stats={episodesData.stats} />
              </ScrollReveal>
            </VStack>
          </Container>
        </Box>

        {/* ── Features Section ── */}
        <Box
          py={{ base: 8, md: 10 }}
          position="relative"
          overflow="hidden"
        >
          {/* Divider line */}
          <Box className="glow-divider" mb={{ base: 8, md: 10 }} />

          {/* Background orbs */}
          <Box
            position="absolute"
            top="30%"
            left="-10%"
            w="350px"
            h="350px"
            borderRadius="full"
            bg="rgba(160,60,20,0.07)"
            filter="blur(80px)"
            pointerEvents="none"
          />
          <Box
            position="absolute"
            bottom="10%"
            right="-5%"
            w="280px"
            h="280px"
            borderRadius="full"
            bg="rgba(255,152,0,0.07)"
            filter="blur(70px)"
            pointerEvents="none"
          />

          <Container maxW="container.xl" position="relative">
            <VStack spacing={{ base: 8, md: 10 }}>
              <ScrollReveal>
                <VStack spacing={3}>
                  <Heading
                    as="h2"
                    fontSize={{ base: "2xl", md: "4xl" }}
                    variant="gradient"
                    textAlign="center"
                  >
                    Why Listen to Namaskar India
                  </Heading>
                  <Text
                    textAlign="center"
                    color="whiteAlpha.700"
                    fontSize={{ base: "md", md: "lg" }}
                    maxW="xl"
                    mx="auto"
                  >
                    Experience history and mythology like never before
                  </Text>
                </VStack>
              </ScrollReveal>

              <ScrollReveal delay={0.2}>
                <SimpleGrid
                  columns={{ base: 1, md: 3 }}
                  spacing={{ base: 6, md: 8 }}
                  px={{ base: 2, md: 0 }}
                >
                  <FeatureCard
                    icon={FaHistory}
                    title="Well-Researched"
                    description="Every episode reflects deep research, presenting history and mythology with accuracy and care."
                  />
                  <FeatureCard
                    icon={FaOm}
                    title="Cultural Context"
                    description="Understand the deep cultural significance and spiritual wisdom behind historical events."
                  />
                  <FeatureCard
                    icon={FaBookReader}
                    title="Engaging Stories"
                    description="History and mythology brought to life through captivating, immersive storytelling."
                  />
                </SimpleGrid>
              </ScrollReveal>
            </VStack>
          </Container>
        </Box>


      </Box>
    </>
  );
};

export default Home;
